/**
 * @fileoverview Per-department Parcel Data tab for the system-web
 *   DepartmentPanel slide-in. Now renders the shared ParcelDataPanel (from
 *   @firesquirrel/ui-admin) in system-admin mode, with the ParcelDataConfigForm
 *   composed as the reconfigure form and ImportProgressDisplay for live
 *   import progress.
 * @module system-web/pages/DepartmentParcelDataTab
 *
 * Responsibilities:
 * - Render the shared ParcelDataPanel in system-admin mode via the
 *   useParcelDataPanelAdapter host adapter hook.
 * - Compose the ParcelDataConfigForm for the reconfigure flow (when the
 *   admin clicks "Reconfigure" or when the department has no config).
 * - Compose the ImportProgressDisplay for live import job progress.
 *
 * Dependencies:
 * - @firesquirrel/ui-admin (ParcelDataPanel)
 * - ../hooks/useParcelDataPanelAdapter (host adapter)
 * - ../components/ParcelDataConfigForm (reconfigure form)
 * - ../components/ImportProgressDisplay (live import progress)
 * - @ui-admin/contexts/ThemeContext
 *
 * Architecture:
 * - Local-first. All reads come from the useParcelDataPanelAdapter hook,
 *   which wraps useDepartmentParcelData (Dexie via liveQuery). No direct
 *   API calls from this component.
 * - The shared ParcelDataPanel handles all rendering of config, source
 *   file + quality, import statistics + quality, tokens, and import
 *   history. This page only adds the reconfigure form and import progress
 *   display.
 *
 * Notes:
 * - This page previously had its own inline implementation of all sections
 *   (PanelSection, ReadField, DataQualityBars, TokenEntitlementEditor,
 *   ImportHistoryTable, etc.). Those are now collapsed into the shared
 *   ParcelDataPanel, which is also used by tenant-web (in department-admin
 *   mode). This eliminates the three-way duplication.
 * - When the department has no Regrid configuration (no parcelImport and
 *   no jurisdiction FIPS), the tab shows the ParcelDataConfigForm inline
 *   instead of the shared panel.
 * - No mock data, no hardcoded URLs, no direct API calls.
 *
 * @component
 * Purpose:     Per-department Regrid parcel data management view for the
 *              system-admin DepartmentPanel slide-in.
 * Props:       tenant — the current department record.
 * Behavior:    liveQuery-driven via useParcelDataPanelAdapter hook;
 *              reconfigure form toggle; admin bypass with confirmation.
 * Data Source: local (Dexie via useParcelDataPanelAdapter hook + tenant prop).
 * Constraints: no direct API calls from this component; all via the adapter.
 * Performance: Dexie liveQuery subscriptions via the adapter hook.
 */

import { useState } from 'react';
import { useTheme } from '@ui-admin/contexts/ThemeContext';
import { ParcelDataPanel } from '@firesquirrel/ui-admin';
import { useParcelDataPanelAdapter } from '../hooks/useParcelDataPanelAdapter';
import ParcelDataConfigForm from '../components/ParcelDataConfigForm';
import ImportProgressDisplay from '../components/ImportProgressDisplay';

export interface DepartmentParcelDataTabProps {
  tenant: any;
}

/**
 * @component DepartmentParcelDataTab
 *
 * Purpose:
 * - Render the per-department Regrid parcel data management view as a tab in
 *   the system-web DepartmentPanel slide-in, using the shared
 *   ParcelDataPanel in system-admin mode.
 *
 * Props:
 * - tenant: The current department record (must contain `_id`; optionally
 *   `parcelImport`, `jurisdiction.fipsCode`, `updateTokens`).
 *
 * Behavior:
 * - Reads from useParcelDataPanelAdapter (which wraps useDepartmentParcelData).
 * - Shows the ParcelDataConfigForm inline when the department has no config.
 * - Shows the reconfigure form when the admin clicks "Reconfigure".
 * - Shows live import progress via ImportProgressDisplay.
 *
 * Data Source:
 * - local (Dexie via useParcelDataPanelAdapter hook + tenant prop).
 *
 * Constraints:
 * - No direct API calls; all mutations go through the adapter hook.
 */
export default function DepartmentParcelDataTab({ tenant }: DepartmentParcelDataTabProps) {
  const { theme } = useTheme();
  const {
    data,
    actions,
    importJob,
    dismissImportJob,
    saveParcelConfig,
    triggerImport,
    cancelImport,
  } = useParcelDataPanelAdapter(tenant);

  const [showReconfig, setShowReconfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [triggeringImport, setTriggeringImport] = useState(false);

  const fips = data.config?.fipsCode ?? null;
  const importPath = data.config?.path ?? null;

  // ── Empty state: no Regrid configuration → show config form ────────
  if (!fips && !importPath) {
    return (
      <div className="px-4 py-3" style={{ background: theme.cardBg }}>
        {data.error && (
          <div className="shrink-0 flex items-center justify-between gap-2 px-3 py-2 mb-3 bg-red-50 text-red-700 text-sm rounded">
            <div className="flex items-center gap-2">
              <span>{data.error}</span>
            </div>
            {actions.clearError && (
              <button
                type="button"
                onClick={actions.clearError}
                className="text-xs text-red-600 hover:text-red-800 underline"
              >
                Dismiss
              </button>
            )}
          </div>
        )}

        <div className="mb-4">
          <h4 className="text-sm font-semibold text-gray-800 mb-2">Parcel Data</h4>
          <p className="text-sm mb-3" style={{ color: theme.textPrimary }}>
            No Regrid parcel data configuration for this department.
          </p>
          <p className="text-xs mb-4" style={{ color: theme.textSecondary }}>
            Configure parcel data below to enable Regrid parcel imports. The
            cache file is auto-matched from the department's jurisdiction
            (county FIPS or state + county). You can also trigger an initial
            import without consuming a token.
          </p>
          <ParcelDataConfigForm
            tenant={tenant}
            onSave={async (config) => {
              setSavingConfig(true);
              const ok = await saveParcelConfig(tenant._id, config);
              setSavingConfig(false);
              return ok;
            }}
            onTriggerImport={async (overrides) => {
              setTriggeringImport(true);
              const jobId = await triggerImport(tenant._id, overrides);
              setTriggeringImport(false);
              return jobId;
            }}
            saving={savingConfig}
            triggering={triggeringImport}
          />
        </div>

        {importJob && (
          <div className="mt-4">
            <ImportProgressDisplay
              job={importJob}
              onCancel={async () => cancelImport(tenant._id, importJob?._id)}
              onDismiss={dismissImportJob}
            />
          </div>
        )}
      </div>
    );
  }

  // ── Build the reconfigure form node ────────────────────────────────
  const reconfigureForm = showReconfig ? (
    <div className="mb-4 p-3 rounded-lg border" style={{ background: theme.expandedBg, borderColor: theme.cardBorder }}>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: theme.textSecondary }}>
          Reconfigure Parcel Data
        </h4>
        <button
          type="button"
          onClick={() => setShowReconfig(false)}
          className="text-xs text-gray-500 hover:text-gray-700"
        >
          Cancel
        </button>
      </div>
      <ParcelDataConfigForm
        tenant={tenant}
        onSave={async (config) => {
          setSavingConfig(true);
          const ok = await saveParcelConfig(tenant._id, config);
          setSavingConfig(false);
          if (ok) setShowReconfig(false);
          return ok;
        }}
        onTriggerImport={async (overrides) => {
          setTriggeringImport(true);
          const jobId = await triggerImport(tenant._id, overrides);
          setTriggeringImport(false);
          return jobId;
        }}
        saving={savingConfig}
        triggering={triggeringImport}
      />
    </div>
  ) : null;

  // ── Build the import progress display node ─────────────────────────
  const importJobDisplay = importJob ? (
    <div className="mb-4">
      <ImportProgressDisplay
        job={importJob}
        onCancel={async () => cancelImport(tenant._id, importJob?._id)}
        onDismiss={dismissImportJob}
      />
    </div>
  ) : null;

  return (
    <div className="px-4 py-3" style={{ background: theme.cardBg }}>
      <ParcelDataPanel
        mode="system-admin"
        data={data}
        actions={actions}
        theme={theme}
        reconfigureForm={reconfigureForm}
        importJobDisplay={importJobDisplay}
        showReconfigure={showReconfig}
        onToggleReconfigure={() => setShowReconfig((s) => !s)}
      />
    </div>
  );
}
